import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
} from "@mui/material";
import styled from "@emotion/styled";
import {
  executeWithCaptcha,
  generateTestId,
  getMediaPath,
} from "app/functions";
import {
  is_valid_israeli_id_number,
  REGEX_PATTERNS,
  VALIDATION_MESSAGES,
} from "app/validation/validation";
import { useSelector } from "react-redux";
import HotSelect from "components/forms/HotSelect";
import HotTextInput from "components/forms/HotTextInput";
import HotTextArea from "components/forms/HotTextArea";
import HotCheckbox from "components/forms/HotCheckbox";
import Recaptcha from "components/services/google/recaptcha";
import HotActionButton from "components/action-button/hotActionButton";
import HotFileUpload from "components/forms/HotFileUpload";
import { useUsersApplicationCreateMutation } from "redux/api/relations.api";
import CloseIcon from "@mui/icons-material/HighlightOffSharp";
import Loader from "components/common/loader";
import { createPath } from "theme";
import { useTheme } from "@emotion/react";
import AnalyticsManager from '../../../../app/analytics';
import HtmlContent from "components/html/HtmlContent";

const useResetAgreementOnEligibilityChange = (
  selectedEligibility,
  setValue,
  setAgreementCheckboxKey
) => {
  const prevEligibilityRef = useRef();

  useEffect(() => {
    const prevEligibilityValue = prevEligibilityRef.current;
    const currentEligibilityValue = selectedEligibility?.value;

    const eligibilityChanged = prevEligibilityValue !== currentEligibilityValue;
    const isFamilyMemberEligibility = (value) => value === 3;

    if (
      eligibilityChanged &&
      (isFamilyMemberEligibility(currentEligibilityValue) ||
        isFamilyMemberEligibility(prevEligibilityValue))
    ) {
      setValue("agreement", false, { shouldValidate: false });
      setAgreementCheckboxKey((key) => key + 1);
    }

    prevEligibilityRef.current = currentEligibilityValue;
  }, [selectedEligibility, setValue, setAgreementCheckboxKey]);
};

const SectionWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "100%",
  background: "linear-gradient(196deg, #8533EB 11.28%, #4001BF 98.81%)",
  padding: "20px",
  [theme.breakpoints.up("md")]: {
    height: "fit-content",
    padding: "40px 50px",
    backgroundSize: "100% auto",
    backgroundRepeat: "no-repeat",
  },
  [theme.breakpoints.up("lg")]: {
    padding: "60px 100px",
  }
}));

const FormWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "left",
  justifyContent: "center",
  flexDirection: "column",
  filter: 'drop-shadow(0px 4.615px 69.231px rgba(45, 85, 199, 0.25))',
  zIndex: 1,
  borderRadius: "24px",
  backgroundColor: "white",
  paddingBottom: "0px",
  [theme.breakpoints.up("md")]: {
    width: "fit-content",
    minHeight: "fit-content",
    backgroundColor: "transparent",
  },
}));


const FormContainer = styled(Box)(({ theme }) => ({
  borderRadius: "24px",
  padding: "0px 20px",
  height: "100%",
  marginTop: "10px",
  background: "white",
  justifyContent: "space-between",
  flexDirection: "column",
  display: "flex",
  [theme.breakpoints.up("md")]: {
    width: "850px",
},
[theme.breakpoints.up("lg")]: {
    width: "1024px",
  },
}));


const FormGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  [theme.breakpoints.up("md")]: {
    display: "grid",
    width: "100%",
    gridTemplateColumns: "repeat(2, 4fr)",
    alignItems: "start",
  },
}));

const FormFieldContainer = styled("div")(({ theme }) => ({
  position: "relative",
  padding: "20px 0",
  marginTop: "10px",
  background: "white",
  [theme.breakpoints.up("md")]: {
    background: "transparent",
  },
}));

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  height: "100%",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: "422px",
  },
}));

const CheckboxesContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: 220,
  },
}));

const NameFieldsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const ButtonContainer = styled("div")(({ theme }) => ({
  margin: "20px",
  [theme.breakpoints.up("md")]: {
    margin: "unset",
    width: "173px",
    right: 0,
    bottom: 0,
    position: "absolute",
  },
}));

const UserApplicationPopup = ({
  disabled,
  isDialogOpen,
  setIsDialogOpen,
  title,
  testID,
  onClose,
  children,
  onSubmit,
}) => {
  return (
    <Dialog
      open={isDialogOpen}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiDialog-paper": {
          padding: "10px 30px",
          position: "relative",
          marginTop: "30px",
        },
      }}
      onClose={() => {
        setIsDialogOpen(false);
        onClose();
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          setIsDialogOpen(false);
          onClose();
        }}
        sx={{
          position: "absolute",
          color: (theme) => theme.palette.grey[900],
          padding: "2px",
          left:4,
          top:4
        }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <DialogTitle testID={generateTestId(testID, "popup-title")}>
        {title}
      </DialogTitle>
      <DialogContent>
      {children}
      </DialogContent>
      <DialogActions disableSpacing>
        <HotActionButton
          size="large"
          onClick={onSubmit}
          disabled={disabled}
          testID={generateTestId(testID, "popup-button")}
          sx={{
            width: "20%",
            margin: "0 auto",
          }}
        >
          אישור
        </HotActionButton>
      </DialogActions>
    </Dialog>
  );
};

const formatDefaultWorkPlace = (workplace) => {
  return {
    value: workplace.id,
    label: workplace.text,
  }
}
const FormSection = ({ data, testID, ref }) => {
  const location = useLocation();
  const isReferrerJoinPage = location.pathname.includes('חבר_מביא_חבר');
  const searchParams = new URLSearchParams(location.search);
  const createdLinkDate = searchParams.get('createdLinkDate');
  const memberId = searchParams.get('memberId');
  const sendPlatform = searchParams.get('sendPlatform');

  const workplaces = useSelector((state) => state.config.WorkPlaces);

  // remove duplicates from workplaces by text
  const uniqueWorkplaces = [];
  const seenTexts = new Set();

  workplaces.forEach((workplace) => {
    if (!seenTexts.has(workplace.text)) {
      uniqueWorkplaces.push(formatDefaultWorkPlace(workplace));
      seenTexts.add(workplace.text);
    }
  });

    

  const text = useSelector((state) => state.config.Translations);
  const defaultValues = data.defaultValues || {};
  const theme = useTheme();
  const [
    createUserApplication,
    {
      isLoading,
      data: createUserApplicationData,
      isSuccess,
      reset: resetCreateUserApplication,
    },
  ] = useUsersApplicationCreateMutation({ skip: true });

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);

  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [agreementCheckboxKey, setAgreementCheckboxKey] = useState(0);

  const types = data.types.map((type) => ({
    value: type.id || type.value,
    label: type.title || type.label,
    ...type,
  }));

  const routes = data.routes.map((route) => ({
    value: route.id || route.value,
    label: route.title || route.label,
    ...route,
  }))
  
  const { handleSubmit, control, watch, getValues, reset, setValue } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      idNumber: "",
      phone: "",
      email: "",
      joinPath: defaultValues.joinPath ? routes.find(x=>x.value==defaultValues.joinPath) : null,
      eligibility: defaultValues.eligibility ? types.find(x=>x.value==defaultValues.eligibility) : null,
      familyMemberId: "",
      workplaceId: defaultValues.workplaceId ? uniqueWorkplaces.find(x=>x.value==defaultValues.workplaceId) : null,
      message: "",
      agreement: false,
      marketingConsent: false,
      declarationConsent: false,
    },
  });

  const selectedEligibility = watch("eligibility");

  useResetAgreementOnEligibilityChange(
    selectedEligibility,
    setValue,
    setAgreementCheckboxKey
  );

  const familyMemberIdConfig = useMemo(() => {
    const showMemberId = (!isReferrerJoinPage && (data?.showMemberId || selectedEligibility?.value === 3));
    const label = data.fields?.family_member_label || "ת.ז חבר מועדון הממליץ";

    return {
       show: showMemberId,
       label:  label,
       required: `יש להזין ${label}`,
       notUserIdMessage: `ת.ז ${label} לא יכולה להיות זהה לת.ז האישית`,
      };
  }, [selectedEligibility, memberId]);

  // const isAllLinksClicked = watch("isAllLinksClicked");

  const onApproval = () => {
    if (data.hideUploadFilePopup) {
      submitUserApplication();
    } else {
      setIsFormDialogOpen(true);
    }
  };

  const resetForm = () => {
    reset();
    setFile(null);
    setIsFormDialogOpen(false);
    setIsMessageDialogOpen(false);
      resetCreateUserApplication();
  };

  const logLeadSuccessAnalytics = () => {
    try {
      const formData = getValues();
      AnalyticsManager.logLeadUser({
          personal_id: formData.idNumber,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          cellphone: formData.phone,
      });
    }catch (e) {
        console.error(e)
    }
  }
  

  useEffect(() => {
    if (isSuccess) {
      setIsMessageDialogOpen(true);
      logLeadSuccessAnalytics();
    }
  }, [isSuccess]);

  const submitUserApplication = async () => {
    setIsFormDialogOpen(false);
    const formData = getValues();
    const slug = data.slug || null;
    executeWithCaptcha(async (token) => {
      const response = await createUserApplication({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        id: String(formData.idNumber).padStart(9, "0"),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        reason: formData.eligibility?.value,
        workPlaceId: formData.workplaceId?.value,
        family_member_id: formData.familyMemberId ? String(formData.familyMemberId).padStart(9, "0") : null,
        route: formData.joinPath?.value,
        message: formData.message.trim(),
        contact_agreement: formData.agreement,
        marketing_consent: formData.marketingConsent,
        declaration_consent: formData.declarationConsent,
        id_file: data.hideUploadFilePopup ? null : file,
        created_link_date: createdLinkDate ? createdLinkDate : null,
        member_id: memberId ? memberId : null,
        send_platform: sendPlatform ? sendPlatform : null,
        
        slug,
        "g-recaptcha-response": token,
      });
    });
  };

  const agreementLabel = useMemo(() => {
    const fieldName = selectedEligibility?.value === 3
      ? 'family_agreement_label'
      : 'agreement_label';
    
    return data.fields?.[fieldName] 
      || data.formFieldsNew?.find(f => f.name === fieldName)?.checkboxText;
  }, [selectedEligibility, data.fields, data.formFieldsNew]);

  const isAddedField = [2, 3].includes(selectedEligibility?.value);

  return (
    <SectionWrapper
      id="customer-join-form-section"
      ref={ref}
      testID={generateTestId(testID, "root")}
      sx={{
        backgroundImage: {md:`url(${getMediaPath(data.backgroundImage)})`},
      }}
    >
      <FormWrapper
        testID={generateTestId(testID, "formWrapper")}
        id="customer-join-form-container-wrapper"
      >
        <FormContainer
          testID={generateTestId(testID, "formContainer")}
          id="customer-join-form-container"
          sx={{
            height: {
              md: isAddedField ? 800 : 700,
            },
            clipPath: {
              md: isAddedField ? createPath(1024, 800) : createPath(1024, 700),
            }
          }}
        >
          <FormFieldContainer
            id="customer-join-form"
            testID={generateTestId(testID, "form")}
            onSubmit={handleSubmit(onApproval)}
          >
            <div>
              <Typography
                variant="title"
                testID={generateTestId(testID, "title")}
                sx={{
                  marginBottom: "12px",
                }}
              >
                {data.leaveDetails}
              </Typography>
              <Typography
                variant="subtitle"
                sx={{
                  marginBottom: "20px",
                }}
                testID={generateTestId(testID, "subtitle")}
              >
                {data.contactYouSoon}
              </Typography>
            </div>
            <FormGrid testID={generateTestId(testID, "formGrid")}>
              <Column>
                <HotSelect
                  name="eligibility"
                  control={control}
                  testID={testID}
                  disabled={!!defaultValues.eligibility}
                  label={data.fields?.eligibility_label || "זכאות הצטרפות"}
                  options={types}
                  rules={{
                    required: VALIDATION_MESSAGES.REQUIRED,
                  }}
                />
                
                {selectedEligibility?.show_workplace_selection === 1 && (
                  <HotSelect
                    name="workplaceId"
                    testID={testID}
                    control={control}
                    label={data.fields?.workplace_label || "מקום עבודה"}
                    disabled={!!defaultValues.workplaceId}
                    options={uniqueWorkplaces}
                    rules={{
                      required: "יש לבחור מקום עבודה",
                    }}
                  />
                )}

                <NameFieldsContainer>
                  <HotTextInput
                    name="firstName"
                    testID={testID}
                    control={control}
                    label={data.fields?.first_name_label || "שם פרטי"}
                    required={true}
                    sx={{
                      flex: { md: 1 },
                    }}
                    rules={{
                      required: VALIDATION_MESSAGES.REQUIRED,
                      pattern: {
                        value: REGEX_PATTERNS.FIRST_NAME,
                        message: VALIDATION_MESSAGES.FIRST_NAME,
                      },
                    }}
                  />
                  <HotTextInput
                    name="lastName"
                    testID={testID}
                    control={control}
                    label={data.fields?.last_name_label || "שם משפחה"}
                    rules={{
                      required: VALIDATION_MESSAGES.REQUIRED,
                      pattern: {
                        value: REGEX_PATTERNS.LAST_NAME,
                        message: VALIDATION_MESSAGES.LAST_NAME,
                      },
                    }}
                    sx={{
                      flex: { md: 1 },
                    }}
                  />
                </NameFieldsContainer>

                <HotTextInput
                  name="idNumber"
                  testID={testID}
                  control={control}
                  label={data.fields?.id_number_label || "תעודת זהות"}
                  required={true}
                  rules={{
                    required: VALIDATION_MESSAGES.REQUIRED,
                    validate: {
                      notUserId: (value) => {
                        if (value === getValues("familyMemberId")) {
                          return "ת.ז בן משפחה לא יכולה להיות זהה לת.ז האישית";
                        }
                        return true;
                      },
                      isValidId: (value) =>
                        is_valid_israeli_id_number(value) ||
                        VALIDATION_MESSAGES.ID,
                    },
                  }}
                />

                {familyMemberIdConfig.show && (
                  <HotTextInput
                    name="familyMemberId"
                    testID={testID}
                    control={control}
                    label={familyMemberIdConfig.label}
                    rules={{
                      required: familyMemberIdConfig.required,
                      validate: {
                        notUserId: (value) => {
                          if (value === getValues("idNumber")) {
                            return familyMemberIdConfig.notUserIdMessage;
                          }
                          return true;
                        },
                        isValidId: (value) =>
                          is_valid_israeli_id_number(value) ||
                          VALIDATION_MESSAGES.ID,
                      },
                    }}
                  />
                )}
              </Column>

              <Column>
                <HotTextInput
                  name="phone"
                  testID={testID}
                  control={control}
                  label={data.fields?.phone_label || "טלפון"}
                  required={true}
                  rules={{
                    required: VALIDATION_MESSAGES.REQUIRED,
                    pattern: {
                      value: REGEX_PATTERNS.CELL,
                      message: VALIDATION_MESSAGES.CELL,
                    },
                  }}
                />

                <HotTextInput
                  name="email"
                  testID={testID}
                  control={control}
                  label={data.fields?.email_label || "אימייל"}
                  rules={{
                    required: VALIDATION_MESSAGES.REQUIRED,
                    pattern: {
                      value: REGEX_PATTERNS.EMAIL,
                      message: VALIDATION_MESSAGES.EMAIL,
                    },
                  }}
                />

                <HotSelect
                  name="joinPath"
                  testID={testID}
                  control={control}
                  label={data.fields?.join_path_label || "מסלול הצטרפות"}
                  options={routes}
                  disabled={!!defaultValues.joinPath}
                  rules={{
                    required: VALIDATION_MESSAGES.REQUIRED,
                  }}
                />

                {/* <HotTextArea
                  name="message"
                  testID={testID}
                  control={control}
                  minRows={5}
                  label={data.fields?.message_label || "הודעה"}
                  sx={{
                    minHeight: 152,
                  }}
                /> */}
              </Column>
            </FormGrid>
          </FormFieldContainer>
          <CheckboxesContainer>
            <HotCheckbox
              key={agreementCheckboxKey}
              name="agreement"
              testID={testID}
              linksNotClickedErrorText={data.checkboxErrorText}
              control={control}
              rules={{
                required: VALIDATION_MESSAGES.REQUIRED,
              }}
              sx={{
                '& .MuiTypography-root': {
                  fontSize: {
                    xs: '12px',
                    sm: '14px',
                  },
                },
              }}
              label={agreementLabel}
            />

            <HotCheckbox
              name="declarationConsent"
              testID={testID}
              linksNotClickedErrorText={data.checkboxErrorText}
              control={control}
              label={data.fields?.declaration_consent_label}
              rules={{
                required: VALIDATION_MESSAGES.REQUIRED,
              }}
              sx={{
                '& .MuiTypography-root': {
                  fontSize: {
                    xs: '12px',
                    sm: '14px',
                  },
                },
              }}
            />

            <HotCheckbox
              name="marketingConsent"
              testID={testID}
              linksNotClickedErrorText={data.checkboxErrorText}
              control={control}
              label={  data.fields?.marketing_consent_label }
              sx={{
                '& .MuiTypography-root': {
                  fontSize: {
                    xs: '12px',
                    sm: '14px',
                  },
                },
              }}
            />
            <Typography 
              component="div" 
              sx={{ 
                fontSize: "14px",
                marginTop: "0",
                [theme.breakpoints.up("md")]: {
                  fontSize: "11px",
                  marginBottom: "20px",
                },
              }}
            >
              </Typography>
          </CheckboxesContainer>
            </FormContainer>
            <ButtonContainer >
          <HotActionButton
            size="large"
            onClick={handleSubmit(onApproval)}
            fullWidth
            testID={testID}
            loading={isLoading}
            disabled={isLoading}
          >
            שלח
          </HotActionButton>
        </ButtonContainer>    
        <Recaptcha isFunction={false} enterprise={true} id="recaptcha-container" />
      </FormWrapper>
      <Box sx={{
        marginTop: '20px',
        fontSize: '12px',
        [theme.breakpoints.down("md")]: {
          color: 'white',
        },
        [theme.breakpoints.up("md")]: {
          maxWidth: '80%',
        },
        [theme.breakpoints.up("lg")]: {
          fontSize: '14px',
        },
        [theme.breakpoints.up("xl")]: {
          maxWidth: '60%',
        },
      }}>
        <HtmlContent   content={data.legalText} />
      </Box>
    {isLoading && <Loader />}

      <UserApplicationPopup
        onClose={() => resetForm()}
        isDialogOpen={isMessageDialogOpen}
        setIsDialogOpen={resetForm}
        title={
          createUserApplicationData?.valid
            ? text.landing_lead_sent_title
            : ""
        }
        testID={testID}
        disabled={false}
        onSubmit={() => resetForm()}
      >
        <div>
          <Typography variant="title" sx={{ marginBottom: "12px" }}>
            {createUserApplicationData?.message ||
              text.joining_client_seccess_msg}
          </Typography>
        </div>
      </UserApplicationPopup>

      <UserApplicationPopup
        onClose={() => submitUserApplication()}
        isDialogOpen={isFormDialogOpen}
        setIsDialogOpen={setIsFormDialogOpen}
        title={selectedEligibility?.form_approve_text}
        testID={testID}
        disabled={!file}
        onSubmit={submitUserApplication}
      >
        <HotFileUpload
          multiple={false}
          testID={testID}
          onChange={(files) => {
            setFile(files[0]);
          }}
          placeholder={text.upload_file_description}
        />
      </UserApplicationPopup>
    </SectionWrapper>
  );
};

export default FormSection;

